import {isAxiosError} from 'axios'

import {MEALME_API_URL} from '@/constants/config'
import {
  FoodProduct,
  MealMeOrderError,
  MealMeProductUnavailableError,
  OrderInformation,
  StoreDetailsResponse,
} from '@/lib/food/foodModel'
import call from '@/utils/api/call'
import UnexpectedError from '@/utils/api/UnexpectedError'
import PermanentError from '@/utils/errors/PermanentError'
import {logError} from '@/utils/logger'
import tryCatch from '@/utils/tryCatch'

import {FoodProductLevelError, FoodStoreLevelError} from './foodErrors'
import {
  AddressToGeoCode,
  type FoodCartItem,
  GeoCodeToAddress,
  PlaceAutocomplete,
  PlaceAutocompleteParams,
  StoreResponse,
} from './foodModel'
import {type DeliveryInfo, FoodSearchFocus, FoodSort, FoodStoreType} from './types'

const FOOD_SORT_TYPE: Record<FoodSort, string> = {
  [FoodSort.Relevance]: 'relevance',
  [FoodSort.Cheapest]: 'cheapest',
  [FoodSort.Distance]: 'distance',
  [FoodSort.Fastest]: 'fastest',
  [FoodSort.Rating]: 'rating',
}
const FOOD_SEARCH_FOCUS_TYPE: Record<FoodSearchFocus, string> = {
  [FoodSearchFocus['']]: '',
  [FoodSearchFocus.Store]: 'store',
  [FoodSearchFocus.Item]: 'item',
}
const FOOD_STORE_TYPE: Record<FoodStoreType, string> = {
  [FoodStoreType['']]: '',
  [FoodStoreType.Restaurant]: 'restaurant',
  [FoodStoreType.Grocery]: 'grocery',
}

export async function getStoreByAddress({
  query,
  longitude,
  latitude,
  store_type,
  user_street_num,
  user_street_name,
  user_city,
  user_state,
  user_zipcode,
  user_country,
  pickup,
  fetch_quotes,
  sort,
  open,
  search_focus,
  default_quote,
  include_utc_hours,
  use_new_db,
  signal,
}: {
  query: string
  longitude: number
  latitude: number
  store_type: FoodStoreType
  user_street_num: string
  user_street_name: string
  user_city: string
  user_state: string
  user_zipcode: string
  user_country: string
  pickup: boolean
  fetch_quotes: boolean
  sort: FoodSort
  open: boolean
  search_focus: FoodSearchFocus
  default_quote: boolean
  include_utc_hours: boolean
  use_new_db: boolean
  signal?: AbortSignal
}) {
  const transformedData = {
    query,
    longitude,
    latitude,
    store_type: FOOD_STORE_TYPE[store_type],
    user_street_num,
    user_street_name,
    user_city,
    user_state,
    user_zipcode,
    user_country,
    pickup,
    fetch_quotes,
    sort: FOOD_SORT_TYPE[sort],
    open,
    search_focus: FOOD_SEARCH_FOCUS_TYPE[search_focus],
    default_quote,
    include_utc_hours,
    use_new_db,
  }

  return call
    .get(`${MEALME_API_URL}/search/store/v3`, {
      params: transformedData,
      signal,
    })
    .then(res => {
      const resData = StoreResponse(res.data)
      return resData
    })
}

export async function convertGeoCodeToAddress({
  signal,
  lat,
  lng,
}: {
  signal?: AbortSignal
  lat: number
  lng: number
}): Promise<GeoCodeToAddress> {
  return call
    .post(
      `${MEALME_API_URL}/location/reverse_geocode/v2`,
      {
        lat,
        lng,
      },
      {
        signal,
      },
    )
    .then(res => {
      // const resData = GeoCodeToAddress(res.data)

      return GeoCodeToAddress(res.data)
    })
}

export async function getAddressByInput({
  signal,
  params,
}: {
  signal?: AbortSignal
  params: PlaceAutocompleteParams
}) {
  return call
    .get(`${MEALME_API_URL}/places/search`, {
      signal,
      params,
    })
    .then(res => {
      const resData = PlaceAutocomplete(res.data)

      return resData
    })
}

export async function convertAddressToGeoCode({
  signal,
  address,
}: {
  signal?: AbortSignal
  address: string
}) {
  return call
    .post(
      `${MEALME_API_URL}/location/geocode/v2`,
      {
        address,
      },
      {
        signal,
      },
    )
    .then(res => {
      const resData = AddressToGeoCode(res.data)

      return resData
    })
}

export async function getFoodProductDetails({
  foodProductId,
  signal,
}: {
  foodProductId: string
  signal?: AbortSignal
}) {
  return call
    .get(`${MEALME_API_URL}/details/product/v2`, {
      params: {
        product_id: foodProductId,
        use_new_db: true,
      },
      signal,
    })
    .then(res => {
      const data = FoodProduct(res.data)
      data.product_id = foodProductId
      return data
    })
}

export async function getStoreDetails({storeId, signal}: {storeId: string; signal?: AbortSignal}) {
  return call
    .get(`${MEALME_API_URL}/search/store/v3`, {
      params: {
        store_id: storeId,
        use_new_db: true,
      },
      signal,
    })
    .then(res => {
      const data = StoreDetailsResponse(res.data)
      invariant(data.store)
      return data.store
    })
    .catch(err => {
      throw new PermanentError('Store details not found', {
        cause: err,
      })
    })
}

export async function previewFoodCart({
  signal,
  items,
  pickup = false,
  userAddress,
  scheduledId,
}: {
  scheduledId: string | undefined
  signal?: AbortSignal
  items: FoodCartItem[]
  userAddress: DeliveryInfo['userAddress']
  pickup: boolean
}) {
  if (items.length === 0) {
    return Promise.resolve(null)
  }

  return call
    .post(
      `${MEALME_API_URL}/order/order/v2`,
      {
        scheduled_id: scheduledId,
        include_final_quote: true,
        place_order: false,
        items: items.map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
          selected_options: item.selectedOptions.map(option => ({
            option_id: option.optionId,
            quantity: option.quantity,
          })),
        })),
        driver_tip_cents: 0,
        pickup_tip_cents: 0,
        pickup,
        charge_user: false,
        disable_sms: true,
        email_receipt_specifications: {
          disable_email: true,
        },
        user_dropoff_notes: 'Leave in lobby',
        user_pickup_notes: '',
        user_latitude: userAddress.latitude || 37.7786357,
        user_longitude: userAddress.longitude || -122.3918135,
        user_street_num: userAddress.street_addr || '188',
        user_street_name: userAddress.street_addr_2 || 'King Street',
        user_city: userAddress.city || 'San Francisco',
        user_state: userAddress.state || 'CA',
        user_country: userAddress.country || 'US',
        user_zipcode: userAddress.zipcode || '94107',
        user_email: 'steve@apple.com',
        user_id: '7B36A9CF',
        user_name: 'Steve Jobs',
        user_phone: 14044442058,
      },
      {signal},
    )
    .then(res => {
      const data = OrderInformation(res.data)
      invariant(items[0])
      data.final_quote.store_id = items[0].storeId // NOTE: wtf mealme why do you return the wrong store id?
      return data
    })
    .catch(err => {
      if (isAxiosError(err)) {
        // https://docs.mealme.ai/reference/create-order-errors
        switch (err.response?.status) {
          case 400:
            // Will not happen: Missing/Invalid required input parameter: <parameter>
            // Will not happen: Parameter user_phone is invalid: phone number must be 10 digits or 11 digits with country code
            // Will not happen: Parameter driver_tip_cents is required for delivery orders.
            // Tested, not happening: Parameter pickup_tip_cents only applies to pickup orders.
            // Tested, not happening: Parameter pickup_tip_cents isn't supported for this store
            throw new UnexpectedError().permanent()
            break
          case 406: {
            // This store is closed
            // Delivery is currently unavailable for this store
            // Pickup is currently unavailable for this store
            // This store is out of range for the provided address
            // Scheduled id is not valid or not available
            const [message] = tryCatch(() => MealMeOrderError(err.response?.data).error)
            if (message) {
              throw new FoodStoreLevelError(message)
            }
            break
          }
          case 420: {
            // Checkout Error: Invalid Products. Either product_id is invalid or product is no longer available
            // TODO: If the product is no longer available, we cannot display the product since we have no information about it => we remove it from the cart, need to let user double check.
            const [data] = tryCatch(() => MealMeProductUnavailableError(err.response?.data))
            if (data) {
              throw new FoodProductLevelError(
                data.invalid_product_ids.map(productId => ({
                  productId,
                  error: 'Product is no longer available',
                })),
              )
            }
            break
          }
          case 422: {
            // Store level:
            // Checkout Error: Does not meet order minimum requirement
            // Checkout Error: Does not meet order maximum requirement
            // => get the minimum/maximum data and create the error message

            // Product level:
            // Checkout Error: Customization requirements have not been met.
            // {
            //   "error": "Checkout Error: Customization requirements have not been met.",
            //   "invalid_customizations": {
            //     "T-Pain Munchie Meal with Chick-N-Tater Melt": [
            //       {
            //         "customization_path": "/Choose a drink",
            //         "min_choice_options": 1,
            //         "max_choice_options": 1,
            //         "selected_options_quantity": 0
            //       }
            //     ]
            //   }
            // }
            // Checkout Error: Invalid option_id -> <option_id>
            // {
            //   "error": "Checkout Error: Invalid option_id -> 1iKFY7Y_6Do7UvLZiUD0RGGeDMkuIrBjEQYh4uoy-X32OoHDYEL7pL705_AZ_u2v8cqYuBRgRXFc1uV_F6QS5Ln9OzuLJ88lbOt6MGQIhqv-4f3euEUjYgdKZ7iXYgFFCACkzIVZ98hSSL7OBKaWwh9eAjbBQAQng0eRVdtvJeJLsB796bXdN3jwrHvOTw4AQ37-D1cug-v5NurrzPgdlfl0hmW1CZfpPGgnARi69RfcjwXbs1CWyCgY3gZX6syr8Bz2TZZZ9q5uVKg2p7YTAc0cjo8vyKHuhAsxU6Es_oe1pJabKO6wGRPZ8it0FnvlC0urEO3PI3vIg1Op1BsROxp0sRdZ6Ty6gkC0r7-gSJC-1w-8INF8XXA82IDtKSGjnDnZPk5EEj7wXPJWrcQ==",
            //   "invalid_option_ids": [
            //     "1iKFY7Y_6Do7UvLZiUD0RGGeDMkuIrBjEQYh4uoy-X32OoHDYEL7pL705_AZ_u2v8cqYuBRgRXFc1uV_F6QS5Ln9OzuLJ88lbOt6MGQIhqv-4f3euEUjYgdKZ7iXYgFFCACkzIVZ98hSSL7OBKaWwh9eAjbBQAQng0eRVdtvJeJLsB796bXdN3jwrHvOTw4AQ37-D1cug-v5NurrzPgdlfl0hmW1CZfpPGgnARi69RfcjwXbs1CWyCgY3gZX6syr8Bz2TZZZ9q5uVKg2p7YTAc0cjo8vyKHuhAsxU6Es_oe1pJabKO6wGRPZ8it0FnvlC0urEO3PI3vIg1Op1BsROxp0sRdZ6Ty6gkC0r7-gSJC-1w-8INF8XXA82IDtKSGjnDnZPk5EEj7wXPJWrcQ=="
            //   ]
            // }
            // Invalid product ID(s) provided
            // {
            //   "error": "Invalid product ID(s) provided"
            // }
            // Will not happen: Product quantity must be greater than zero
            const [errorMessage] = tryCatch(() => MealMeOrderError(err.response?.data).error)
            if (errorMessage) {
              if (
                errorMessage.startsWith(
                  'Checkout Error: Customization requirements have not been met.',
                )
              ) {
                // TODO: improve this error message: try to match the customization path and name to get the product id
                throw new UnexpectedError().permanent()
              } else if (errorMessage.startsWith('Checkout Error: Invalid option_id ->')) {
                // TODO: improve this error message: try to match the option id to get the product id
                throw new UnexpectedError().permanent()
              } else if (errorMessage.startsWith('Invalid product ID(s) provided')) {
                // TODO: improve this error message: try to fetch all the products and find out which one is invalid
                // TODO: If the product is no longer available, we cannot display the product since we have no information about it => we remove it from the cart, need to let user double check.
                throw new UnexpectedError().permanent()
              } else if (
                errorMessage.startsWith('Checkout Error: Does not meet order minimum requirement')
              ) {
                // TODO: improve this error message: get the minimum/maximum data and create the error message
                throw new UnexpectedError().permanent()
              } else if (
                errorMessage.startsWith('Checkout Error: Does not meet order maximum requirement')
              ) {
                // TODO: improve this error message: get the minimum/maximum data and create the error message
                throw new UnexpectedError().permanent()
              }
            }
            break
          }
          case undefined:
        }
      }
      logError(err)
      throw new UnexpectedError().permanent()
    })
}
